import { useSyncExternalStore } from 'react'
import { startAmbient, stopAmbient } from '@/lib/ambient'

export type LightMode = 'normal' | 'red'
export type MotionMode = 'full' | 'reduced'

export type Preferences = {
    light: LightMode
    motion: MotionMode
    sound: boolean
    ready: boolean
}

const LIGHT_KEY = 'aphelion:light'
const MOTION_KEY = 'aphelion:motion'

const defaults: Preferences = {
    light: 'normal',
    motion: 'full',
    sound: false,
    ready: false
}

let state: Preferences = defaults
let fadeTimer: number | undefined

const listeners = new Set<() => void>()

const emit = (): void => {
    listeners.forEach(listener => listener())
}

const persist = (key: string, value: string): void => {
    try {
        localStorage.setItem(key, value)
    }
    catch {
        // Private browsing can refuse storage; the choice still applies for this visit.
    }
}

export const subscribePreferences = (listener: () => void): (() => void) => {
    listeners.add(listener)

    return () => {
        listeners.delete(listener)
    }
}

export const getPreferences = (): Preferences => state

const getServerPreferences = (): Preferences => defaults

/** Reads the choices the head script already applied to <html>. Call once after mount. */
export const initPreferences = (): void => {
    if (state.ready) return

    const root = document.documentElement

    state = {
        light: root.getAttribute('data-light') === 'red' ? 'red' : 'normal',
        motion: root.getAttribute('data-motion') === 'reduced' ? 'reduced' : 'full',
        sound: false,
        ready: true
    }

    emit()
}

export const setLight = (light: LightMode): void => {
    const root = document.documentElement

    root.classList.add('light-fading')
    window.clearTimeout(fadeTimer)
    fadeTimer = window.setTimeout(() => root.classList.remove('light-fading'), 1200)

    if (light === 'red') root.setAttribute('data-light', 'red')
    else root.removeAttribute('data-light')

    persist(LIGHT_KEY, light)
    state = { ...state, light }
    emit()
}

export const toggleLight = (): void => {
    setLight(state.light === 'red' ? 'normal' : 'red')
}

export const setMotion = (motion: MotionMode): void => {
    document.documentElement.setAttribute('data-motion', motion)
    persist(MOTION_KEY, motion)
    state = { ...state, motion }
    emit()
}

export const setSound = (sound: boolean): void => {
    if (sound) void startAmbient()
    else stopAmbient()

    state = { ...state, sound }
    emit()
}

export const usePreferences = (): Preferences => {
    return useSyncExternalStore(subscribePreferences, getPreferences, getServerPreferences)
}

export const useReducedMotion = (): boolean => {
    return usePreferences().motion === 'reduced'
}

/** For non-React code (render loops) that needs the current motion setting. */
export const prefersReducedMotion = (): boolean => {
    // Child effects run before _app calls initPreferences, so read the head script's choice until then.
    if (!state.ready && typeof document !== 'undefined') return document.documentElement.getAttribute('data-motion') === 'reduced'

    return state.motion === 'reduced'
}
